import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Button,
  Group,
  LoadingOverlay,
  Modal,
  Pagination,
  Text,
} from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type {
  TaskListItem,
  TrackTaskDetail,
} from "@labeling-tools/api/field-rep/generated/types.gen";
import {
  fetchNextTask,
  isTaskUnavailable,
  listProductSets,
  listTasks,
  openTask,
  releaseTask,
  taskQueryKey,
} from "@/utils/api";
import { notifyError, notifySuccess, notifyWarning } from "@/utils/notify";
import { withTaskListFilters } from "@/utils/taskListFilters";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useLockedAction } from "@/hooks/useLockedAction";
import { useTaskListFilters } from "@/hooks/useTaskListFilters";
import {
  TaskRowAction,
  taskRowAction,
} from "@/components/task-list/taskRowAction";
import { ProductSetSelect } from "@/components/task-list/ProductSetSelect";
import { HEADER_HEIGHT } from "@/config/constants";

const PAGE_SIZE = 50;
// Self refresh every 20 seconds, same as the Lebron jobs page before.
const LIST_REFRESH_MS = 20 * 1000;

const TABLE_MIN_WIDTH = "min-w-[1272px]";

const ACTION_LABELS: Record<TaskRowAction, string> = {
  [TaskRowAction.Open]: "Open",
  [TaskRowAction.Resume]: "Resume",
  [TaskRowAction.Release]: "Release",
};

const formatDateTime = (iso: string | null): string =>
  iso
    ? new Date(iso).toLocaleString(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
      })
    : "";

const StatusPill = ({ task }: { task: TaskListItem }) =>
  task.status === "IN_REVIEW" ? (
    <span className="rounded-full bg-[#FEF3C7] px-2.5 py-0.5 text-xs font-medium text-[#92400E]">
      In review
    </span>
  ) : (
    <span className="rounded-full bg-[#F1F5F9] px-2.5 py-0.5 text-xs font-medium text-[#475569]">
      Pending
    </span>
  );

/** The task's assignee; the logged-in labeler's own task is marked with a dot and the accent. */
const AssigneeCell = ({
  task,
  isMine,
}: {
  task: TaskListItem;
  isMine: boolean;
}) =>
  isMine ? (
    <span className="flex min-w-0 items-center gap-2 font-medium text-[#5A6EFA]">
      <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#5A6EFA]" />
      <span className="truncate">{task.reviewer?.email}</span>
      <span className="sr-only">(you)</span>
    </span>
  ) : (
    <span className="text-[#475569]">{task.reviewer?.email}</span>
  );

export const TaskListPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const currentUserQuery = useCurrentUser();
  const currentUser = currentUserQuery.data;
  const { isLocked, run } = useLockedAction();
  const [filters, setFilters] = useTaskListFilters();
  const selectedProductSetId = filters.productSetId;
  const [page, setPage] = useState(1);

  // Back to page 1 whenever the product set changes.
  const [pageProductSetId, setPageProductSetId] =
    useState(selectedProductSetId);
  if (pageProductSetId !== selectedProductSetId) {
    setPageProductSetId(selectedProductSetId);
    setPage(1);
  }

  // Staff confirm before releasing someone else's task: the holder may still be labeling it.
  // The task stays set while the dialog animates closed, so its text doesn't blank out.
  const [taskToRelease, setTaskToRelease] = useState<TaskListItem | null>(null);
  const [isReleaseDialogOpen, setIsReleaseDialogOpen] = useState(false);

  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const isStaff = currentUser?.is_staff === true;

  const tasksQuery = useQuery({
    queryKey: ["tasks", page, selectedProductSetId, isStaff],
    queryFn: () =>
      listTasks(page, PAGE_SIZE, selectedProductSetId, { isStaff }),
    // Staff and labelers see different tasks, so disable until we know the user.
    enabled: !currentUserQuery.isPending,
    placeholderData: keepPreviousData,
    refetchOnMount: "always",
    refetchInterval: LIST_REFRESH_MS,
  });

  // Refreshed with the task list, as its counts change as tasks are completed.
  const productSetsQuery = useQuery({
    queryKey: ["product-sets"],
    queryFn: listProductSets,
    refetchOnMount: "always",
    refetchInterval: LIST_REFRESH_MS,
  });

  const productSets = productSetsQuery.data;

  const tasks = tasksQuery.data?.items ?? [];
  const jobsById = useMemo(
    () => new Map((tasksQuery.data?.jobs ?? []).map((job) => [job.id, job])),
    [tasksQuery.data],
  );
  const getRowAction = (task: TaskListItem) =>
    currentUser ? taskRowAction(task, currentUser) : null;

  const total = tasksQuery.data?.total ?? 0;
  const totalPages = Math.max(tasksQuery.data?.total_pages ?? 1, 1);

  // Tasks will disappear from the list once labeled, so the page currently being viewed might get
  // emptied. Move back to the last page.
  useEffect(() => {
    if (tasksQuery.data && page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages, tasksQuery.data]);

  const refreshList = () => {
    void queryClient.invalidateQueries({ queryKey: ["tasks"] });
    void queryClient.invalidateQueries({ queryKey: ["product-sets"] });
  };

  const goToTask = (task: TrackTaskDetail) => {
    // The page is gone, no need to proceed.
    if (!isMountedRef.current) {
      return;
    }

    queryClient.removeQueries({ queryKey: ["task"] });
    queryClient.setQueryData(taskQueryKey(task.id), task);
    navigate(withTaskListFilters(`/tasks/${task.id}`, filters));
  };

  const handleStartLabeling = (productSetId: string) =>
    run(async () => {
      let task;
      try {
        task = await fetchNextTask(productSetId);
      } catch (error) {
        notifyError("Couldn't start labeling", error);
        return;
      }

      if (!task) {
        notifySuccess(
          "All done",
          "There are no tasks waiting in this product set. Check back later.",
        );
        refreshList();
        return;
      }

      goToTask(task);
    });

  const handleOpen = (taskId: string) =>
    run(async () => {
      let task;
      try {
        task = await openTask(taskId);
      } catch (error) {
        if (isTaskUnavailable(error)) {
          notifyWarning("This task isn't available any more", error);
          refreshList();
          return;
        }
        notifyError("Couldn't open the task", error);
        return;
      }

      goToTask(task);
    });

  const handleRelease = (taskId: string) =>
    run(async () => {
      try {
        await releaseTask(taskId);
        notifySuccess("Task released", "The labeler can no longer submit it.");
      } catch (error) {
        if (isTaskUnavailable(error)) {
          notifyWarning("This task is no longer held", error);
        } else {
          notifyError("Couldn't release the task", error);
        }
      }

      refreshList();
    });

  const handleAction = (action: TaskRowAction, task: TaskListItem) => {
    if (action === TaskRowAction.Release) {
      setTaskToRelease(task);
      setIsReleaseDialogOpen(true);
    } else {
      void handleOpen(task.id);
    }
  };

  const confirmRelease = () => {
    if (!taskToRelease) {
      return;
    }

    void handleRelease(taskToRelease.id);
    setIsReleaseDialogOpen(false);
  };

  const isActionDisabled = isLocked || tasksQuery.isPlaceholderData;

  // Labelers see read only tasks: their work starts from "Start labeling".
  // Staff see every unfinished task. A task under review can't be opened, its only
  // action is Release.
  const rows = tasks.map((task) => {
    const job = jobsById.get(task.job_id);
    const action = getRowAction(task);
    const isRelease = action === TaskRowAction.Release;
    const isClickable = action !== null && !isRelease && !isActionDisabled;

    return (
      <tr
        key={task.id}
        className={
          isClickable
            ? "cursor-pointer border-b border-[#F1F5F9] hover:bg-[#F8FAFC]"
            : "border-b border-[#F1F5F9]"
        }
        onClick={isClickable ? () => handleAction(action, task) : undefined}
      >
        <td className="truncate py-3.5 pl-7 pr-4 text-sm text-[#1E293B]">
          {job?.description}
        </td>
        <td
          className="truncate px-4 py-3.5 text-sm text-[#1E293B]"
          title={job?.product_set?.name}
        >
          {job?.product_set?.name ?? <span className="text-[#94A3B8]">—</span>}
        </td>
        <td className="px-4 py-3.5 text-sm text-[#64748B]">
          {formatDateTime(job?.recording_started_at ?? null)}
        </td>
        <td className="px-4 py-3.5">
          <StatusPill task={task} />
        </td>
        <td className="px-4 py-3.5 text-sm text-[#64748B]">
          {formatDateTime(task.assigned_at)}
        </td>
        <td className="truncate px-4 py-3.5 text-sm">
          <AssigneeCell task={task} isMine={action === TaskRowAction.Resume} />
        </td>
        <td className="py-2 pl-4 pr-7 text-right">
          {action && (
            <Button
              size="xs"
              variant={isRelease ? "subtle" : "light"}
              color={isRelease ? "gray" : "#5A6EFA"}
              disabled={isActionDisabled}
              onClick={(event) => {
                // The row handles the same click otherwise.
                event.stopPropagation();
                handleAction(action, task);
              }}
            >
              {ACTION_LABELS[action]}
            </Button>
          )}
        </td>
      </tr>
    );
  });

  return (
    <div
      className="flex flex-col gap-5 bg-[#F4F7F9] p-6 text-left"
      style={{ height: `calc(100vh - ${HEADER_HEIGHT}px)` }}
    >
      <section className="flex flex-wrap items-center justify-between gap-8 rounded-[20px] border-2 border-[#5A6EFA] bg-white px-8 py-7 shadow-[0_0_0_4px_#E3E7FE]">
        <div className="flex flex-col gap-1.5">
          <h2 className="text-2xl font-bold tracking-[-0.01em] text-[#1E293B]">
            Ready to label?
          </h2>
          <p className="text-[15px] text-[#64748B]">
            To start labeling, pick a product set and press Start labeling.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <ProductSetSelect
            productSets={productSets}
            selectedProductSetId={selectedProductSetId}
            disabled={isLocked}
            onSelect={(productSetId) =>
              setFilters({ ...filters, productSetId })
            }
          />
          <Button
            size="md"
            radius="md"
            color="#5A6EFA"
            onClick={() => {
              if (selectedProductSetId) {
                void handleStartLabeling(selectedProductSetId);
              }
            }}
            disabled={isLocked || !selectedProductSetId}
          >
            Start labeling
          </Button>
        </div>
      </section>

      <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden rounded-[20px] border border-[#E6EBEF] bg-white">
        <LoadingOverlay visible={tasksQuery.isPending} />

        <h2 className="flex items-baseline gap-3 px-7 py-6 text-[22px] font-bold tracking-[-0.01em] text-[#1E293B]">
          Tasks to label
          {tasksQuery.data && (
            <span className="text-base font-medium tabular-nums text-[#64748B]">
              {total.toLocaleString()}
            </span>
          )}
        </h2>

        {(tasksQuery.error || productSetsQuery.error) && (
          <div className="space-y-3 px-7 pb-4">
            {tasksQuery.error && (
              <Alert
                color="red"
                icon={<IconAlertCircle size={18} />}
                title="Couldn't load the tasks"
              >
                {tasksQuery.error.message}
              </Alert>
            )}
            {productSetsQuery.error && (
              <Alert
                color="red"
                icon={<IconAlertCircle size={18} />}
                title="Couldn't load the product sets"
              >
                {productSetsQuery.error.message}
              </Alert>
            )}
          </div>
        )}

        <div className="min-h-0 flex-1 overflow-auto border-t border-[#E6EBEF]">
          {tasksQuery.data && total === 0 ? (
            <p className="px-7 py-10 text-sm text-[#64748B]">
              No tasks waiting to be labeled. Check back later.
            </p>
          ) : (
            <table
              className={`w-full ${TABLE_MIN_WIDTH} table-fixed border-collapse text-left`}
            >
              <colgroup>
                <col />
                <col className="w-48" />
                <col className="w-44" />
                <col className="w-28" />
                <col className="w-44" />
                <col className="w-64" />
                <col className="w-28" />
              </colgroup>
              <thead className="sticky top-0 z-10 bg-white">
                <tr className="text-[13px] text-[#64748B] [&>th]:shadow-[inset_0_-1px_0_#E6EBEF]">
                  <th className="py-3 pl-7 pr-4 font-medium">Job</th>
                  <th className="px-4 py-3 font-medium">Product set</th>
                  <th className="px-4 py-3 font-medium">Recorded</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Started</th>
                  <th className="px-4 py-3 font-medium">Assigned to</th>
                  <th className="py-3 pl-4 pr-7">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>{rows}</tbody>
            </table>
          )}
        </div>

        {totalPages > 1 && (
          <div className="flex justify-center border-t border-[#E6EBEF] px-7 py-3">
            <Pagination
              total={totalPages}
              value={page}
              onChange={setPage}
              size="sm"
              color="#5A6EFA"
            />
          </div>
        )}
      </div>

      <Modal
        opened={isReleaseDialogOpen}
        onClose={() => setIsReleaseDialogOpen(false)}
        title="Release this task?"
        centered
      >
        <Text size="sm">
          {taskToRelease?.reviewer?.email || "Another labeler"} is labeling a
          track from{" "}
          {(taskToRelease && jobsById.get(taskToRelease.job_id)?.description) ||
            "this job"}
          . If you release it, their answer won&apos;t be saved.
        </Text>
        <Group justify="flex-end" mt="lg">
          <Button
            variant="default"
            onClick={() => setIsReleaseDialogOpen(false)}
          >
            Cancel
          </Button>
          <Button color="red" onClick={confirmRelease}>
            Release
          </Button>
        </Group>
      </Modal>
    </div>
  );
};
